import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowRight, Award, Truck, RotateCcw, Sparkles } from "lucide-react";
import { NewBadge } from "@/components/NewBadge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `₹${n.toLocaleString("en-IN")}`;

const OCCASIONS = [
  { key: "Kurtis", label: "Everyday", note: "Softer silhouettes for the daily rhythm." },
  { key: "Dresses", label: "Office", note: "Composed, tailored, unstuffy." },
  { key: "Anarkali", label: "Wedding guest", note: "Sparingly embellished, generously graceful." },
  { key: "Festive Wear", label: "Festive", note: "Warm, luminous, unmistakable." },
];

const PILLARS = [
  { Icon: Award, title: "Manufacturer-direct", note: "Cut on our own tables. No third-party markup." },
  { Icon: Truck, title: "Free shipping ₹2,500+", note: "Insured pan-India delivery in 3–5 days." },
  { Icon: RotateCcw, title: "7-day easy exchange", note: "Sizing swaps or a full refund, on us." },
  { Icon: Sparkles, title: "Hand-finished", note: "Every piece checked by a senior tailor before dispatch." },
];

export default function Home() {
  const [products, setProducts] = useState([]);
  useEffect(() => { axios.get(`${API}/products`).then(r => setProducts(r.data)); }, []);
  const hero = products[0];
  const arrivals = products;
  const bestsellers = [...products].sort((a, b) => (b.rating || 0) - (a.rating || 0)).slice(0, 4);

  return (
    <div className="home-page">
      {hero && (
        <section className="lux-hero" data-testid="home-hero" style={{ backgroundImage: `linear-gradient(105deg,rgba(28,18,14,.72) 0%,rgba(28,18,14,.22) 55%,rgba(28,18,14,0) 100%),url('${hero.image}')` }}>
          <div className="lux-hero-inner">
            <span className="lux-eyebrow gold">Autumn atelier · Mumbai</span>
            <h1>The <em>quiet</em> luxury<br />of ready-to-wear.</h1>
            <p>Small-batch garments shaped in our Govandi atelier — for women who prefer meaning to noise.</p>
            <Link to="/shop" className="lux-cta" data-testid="hero-shop-button">Explore the collection <ArrowRight size={16} /></Link>
          </div>
          <div className="lux-hero-caption">
            <span>Est. 1998</span>
            <span>{hero.name}</span>
          </div>
        </section>
      )}

      <section className="lux-marquee" data-testid="category-marquee" aria-label="Shop by category">
        <div className="marquee-track">
          {["Kurtis", "Anarkali", "Dresses", "Festive Wear", "Everyday edit", "Wedding guest"].concat(["Kurtis", "Anarkali", "Dresses", "Festive Wear", "Everyday edit", "Wedding guest"]).map((w, i) => (
            <Link key={`${w}-${i < 6 ? "a" : "b"}`} to={`/shop?category=${encodeURIComponent(["Kurtis","Anarkali","Dresses","Festive Wear"].includes(w) ? w : "")}`} className="marquee-item">{w} <span aria-hidden>◆</span></Link>
          ))}
        </div>
      </section>

      <section className="lux-section" data-testid="new-arrivals-section">
        <div className="lux-section-head">
          <div>
            <span className="lux-eyebrow dark">New this week</span>
            <h2>Pieces we couldn't wait to release.</h2>
          </div>
          <Link to="/shop" className="lux-text-link">See the full shop →</Link>
        </div>
        <div className="lux-grid" data-testid="arrivals-grid">
          {arrivals.map(p => (
            <Link key={p.id} to={`/product/${p.id}`} className="lux-card" data-testid={`arrival-card-${p.id}`}>
              <div className="lux-card-image">
                <img src={p.image} alt={p.name} loading="lazy" />
                <NewBadge product={p} />
                <span className="lux-card-hover">View piece →</span>
              </div>
              <div className="lux-card-info">
                <p className="lux-card-cat">{p.category}</p>
                <h3>{p.name}</h3>
                <p className="lux-card-price">{money(p.price)} <del>{money(p.original_price)}</del></p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="lux-occasion" data-testid="occasion-section">
        <div className="lux-section-head centered">
          <span className="lux-eyebrow">Shop by mood</span>
          <h2>Different rooms of your life.</h2>
        </div>
        <div className="occasion-grid">
          {OCCASIONS.map(o => (
            <Link key={o.key} to={`/shop?category=${encodeURIComponent(o.key)}`} className="occasion-card" data-testid={`occasion-${o.label.toLowerCase().replace(/ /g, "-")}`}>
              <h3>{o.label}</h3>
              <p>{o.note}</p>
              <span>Explore →</span>
            </Link>
          ))}
        </div>
      </section>

      <section className="lux-pillars" data-testid="pillars-section">
        {PILLARS.map(({ Icon, title, note }) => (
          <div key={title} className="pillar">
            <Icon size={20} />
            <div><strong>{title}</strong><small>{note}</small></div>
          </div>
        ))}
      </section>

      {bestsellers.length > 0 && (
        <section className="lux-section" data-testid="bestsellers-section">
          <div className="lux-section-head">
            <div>
              <span className="lux-eyebrow dark">Most loved</span>
              <h2>Bestsellers this season.</h2>
            </div>
            <Link to="/shop" className="lux-text-link">Browse all →</Link>
          </div>
          <div className="lux-carousel">
            {bestsellers.map(p => (
              <Link key={p.id} to={`/product/${p.id}`} className="best-card" data-testid={`bestseller-${p.id}`}>
                <div className="best-image"><img src={p.image} alt={p.name} /><NewBadge product={p} /></div>
                <div className="best-info">
                  <p className="lux-card-cat">{p.category}</p>
                  <h3>{p.name}</h3>
                  <p className="lux-card-price">{money(p.price)}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="lux-story" data-testid="story-section">
        <div className="story-inner">
          <span className="lux-eyebrow">Inside the atelier</span>
          <h2>Cut, stitched, checked, and packed by a team of eleven.</h2>
          <p>Since 1998, our Govandi workshop has produced clothing for boutiques across India. Arisha Creations is our own line — the pieces we've always wanted to make ourselves.</p>
          <Link to="/shop" className="lux-text-link">See what we're making →</Link>
        </div>
      </section>
    </div>
  );
}
